"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Les 4 "super-pouvoirs" débloqués par Claude Code, qui s'allument un par un au scroll.
const POWERS = [
    {
        icon: "code-scan-bold-duotone",
        titleKey: "ClaudeCodeSecret_power_1_title",
        descKey: "ClaudeCodeSecret_power_1_desc",
    },
    {
        icon: "bug-bold-duotone",
        titleKey: "ClaudeCodeSecret_power_2_title",
        descKey: "ClaudeCodeSecret_power_2_desc",
    },
    {
        icon: "magic-stick-3-bold",
        titleKey: "ClaudeCodeSecret_power_3_title",
        descKey: "ClaudeCodeSecret_power_3_desc",
    },
    {
        icon: "shield-check-bold-duotone",
        titleKey: "ClaudeCodeSecret_power_4_title",
        descKey: "ClaudeCodeSecret_power_4_desc",
    },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" },
    },
};

export default function ClaudeCodeSecretWeapon() {
    const t = useTranslations();

    return (
        <section id="secret-weapon" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("ClaudeCodeSecret_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("ClaudeCodeSecret_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/15 ring-1 ring-orange-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="bolt-bold-duotone"
                            className="w-3.5 h-3.5 text-orange-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-300">
                            {t("ClaudeCodeSecret_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("ClaudeCodeSecret_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des super-pouvoirs */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 gap-4 sm:gap-5"
                    >
                        {POWERS.map((power) => (
                            <motion.div
                                key={power.titleKey}
                                variants={cardItem}
                                className="flex items-start gap-4 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-orange-400/20 to-amber-500/10 ring-1 ring-orange-400/30 flex items-center justify-center">
                                    <SolarIcon
                                        name={power.icon}
                                        className="w-5 h-5 text-orange-300"
                                    />
                                </div>
                                <div>
                                    <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                        {t(power.titleKey)}
                                    </h4>
                                    <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                        {t(power.descKey)}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
